import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Divider,
  useToast,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
  Flex,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
  date as commonDate,
  utils as commonUtils,
  finleyAssertUnreachable,
} from '@finley-technologies/common';

import { DeliverablesOrgLevelCommentsViewGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { ErrorBoundary } from '~/contexts/MonitoringProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { useNotifyNewCommentForDeliverableContainerMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/NotifyNewCommentForDeliverableContainer.gql.generated';
import {
  FilterButton,
  FilterType,
} from '~/core/routes/deliverables/FilterButton';
import { useGetAllDeliverablesThreadsByOrganizationQuery } from '~/core/routes/deliverables/queries/GetAllDeliverablesThreadsByOrganization.gql.generated';
import { CommentThread } from '~/packages/common-frontend/components/Comments/CommentThread';
import { Counter } from '~/packages/common-frontend/components/Counter';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';

const RouteQueryParamsStruct = z.object({
  comments: z.string({ message: 'Missing URL parameter' }).optional(),
});

const OverviewUpdatesDrawerBody = ({
  threads,
  refetchThreads,
}: {
  threads: DeliverablesOrgLevelCommentsViewGQLType[];
  refetchThreads: () => void;
}) => {
  const { user } = useUserAuthOrThrow();

  const toast = useToast();
  const [
    notifyNewCommentForDeliverableContainerMutation,
  ] = useNotifyNewCommentForDeliverableContainerMutation({
    fetchPolicy: 'network-only',
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const sortedThreads = useMemo(
    () =>
      [...threads].sort((a, b) => b.thread.created_at - a.thread.created_at),
    [threads],
  );

  return (
    <Flex direction="column" gap="1rem" alignItems="flex-start">
      {sortedThreads.length === 0 && (
        <Typography variant="base-muted">There are no comments.</Typography>
      )}
      {sortedThreads.map((threadWithMetadata) => {
        const {
          thread,
          creditFacilityName,
          containerName,
          containerDueDate,
          containerId,
          receiptId,
        } = threadWithMetadata;

        const name = `${commonUtils.toTitleCase(
          containerName,
        )} - ${commonDate.fullDateStrToShortMonthDayString(containerDueDate)}`;

        return (
          <CommentThread
            key={thread.threadId}
            thread={thread}
            refetchThread={refetchThreads}
            onCreateNewComment={async (newCommentId) => {
              await notifyNewCommentForDeliverableContainerMutation({
                variables: {
                  containerId,
                  commentId: newCommentId,
                  receiptId,
                },
              });
              refetchThreads();
            }}
          >
            <Flex justifyContent="space-between" gap="0.5rem">
              {(() => {
                if (user.userType === 'CAPITAL_PROVIDER' && receiptId) {
                  return (
                    <Link
                      fontSize="sm"
                      fontWeight="bold"
                      color="blue.400"
                      href={`/deliverables/${containerId}/final-output-report-versions/${receiptId}?comments=true`}
                    >
                      {name}
                    </Link>
                  );
                }
                if (user.userType === 'BORROWER') {
                  return (
                    <Link
                      fontSize="sm"
                      fontWeight="bold"
                      color="blue.400"
                      href={`/deliverables/${containerId}?comments=true`}
                    >
                      {name}
                    </Link>
                  );
                }
                return <Typography variant="detail">{name}</Typography>;
              })()}
              <Typography variant="detail">{creditFacilityName}</Typography>
            </Flex>
            <Divider />
          </CommentThread>
        );
      })}
    </Flex>
  );
};

type OverviewUpdatesDrawerBaseProps = {
  externalOrgOptions: { value: string; label: string }[];
};
const OverviewUpdatesDrawerBase = ({
  externalOrgOptions,
}: OverviewUpdatesDrawerBaseProps) => {
  const { user } = useUserAuthOrThrow();

  // Open drawer when `?comments=true` query param is present
  const [queryParams] = useSearchParams();
  const { comments: commentsSearchParams } = RouteQueryParamsStruct.parse(
    Object.fromEntries(queryParams),
  );

  const updatesDrawerControls = useDisclosure({
    defaultIsOpen: commentsSearchParams === 'true',
  });

  const threadsQuery = useGetAllDeliverablesThreadsByOrganizationQuery({
    variables: { organizationId: user.organization.id },
  });
  if (threadsQuery.error) throw threadsQuery.error;

  // apply filter here
  const [currentFilter, setCurrentFilter] = useState<FilterType>();
  const threads = useMemo(() => {
    if (!threadsQuery.data?.deliverablesThreadsByOrganization) return [];
    if (!currentFilter)
      return threadsQuery.data.deliverablesThreadsByOrganization;

    const {
      audienceFilter: audienceType,
      externalParticipantIds,
    } = currentFilter;
    const filteredThreads = threadsQuery.data.deliverablesThreadsByOrganization.filter(
      (t) => {
        const orgIdsForThread = t.thread.threadPermissions.map(
          (tp) => tp.organizationId,
        );

        const threadIsInternal =
          orgIdsForThread.length === 1 &&
          orgIdsForThread[0] === user.organization.id;
        const threadIsExternal =
          _.difference(
            [...externalParticipantIds].sort(),
            [...orgIdsForThread].sort(),
          ).length === 0;

        switch (audienceType) {
          case 'internal':
            return threadIsInternal;
          case 'external':
            return threadIsExternal;
          case 'all':
            return threadIsInternal || threadIsExternal;
          default:
            return finleyAssertUnreachable(audienceType);
        }
      },
    );

    return filteredThreads;
  }, [
    currentFilter,
    threadsQuery.data?.deliverablesThreadsByOrganization,
    user.organization.id,
  ]);

  const pinnedThreads = useMemo(
    () => threads.filter((thread) => thread.thread.pinned),
    [threads],
  );

  const [tabIndex, setTabIndex] = useState(0);

  return (
    <>
      {/* Trigger */}
      <IconButtonV2
        isLoading={threadsQuery.loading}
        variant="primary-muted"
        placement="bottom"
        aria-label="Updates"
        icon={<Icon icon="message-lines" />}
        isActive={updatesDrawerControls.isOpen}
        onClick={updatesDrawerControls.onToggle}
      />

      {/* Content */}
      <Drawer {...updatesDrawerControls} placement="right" size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Updates</DrawerHeader>

          <DrawerBody>
            <Tabs
              size="sm"
              index={pinnedThreads.length === 0 ? 0 : tabIndex}
              onChange={(index) => setTabIndex(index)}
            >
              <Flex gap="0.5rem" alignItems="center">
                <TabList flexGrow={1}>
                  <Tab marginTop={0}>All</Tab>
                  {pinnedThreads.length > 0 && (
                    <Tab marginTop={0} gap="0.5rem">
                      <span>Pinned</span>
                      <Counter count={pinnedThreads.length} />
                    </Tab>
                  )}
                </TabList>
                {externalOrgOptions.length > 0 && (
                  <FilterButton
                    externalOrgOptions={externalOrgOptions}
                    onApply={setCurrentFilter}
                    currentFilter={currentFilter}
                  />
                )}
              </Flex>

              <TabPanels py="1rem">
                <TabPanel padding="0">
                  <OverviewUpdatesDrawerBody
                    threads={threads}
                    refetchThreads={threadsQuery.refetch}
                  />
                </TabPanel>
                {pinnedThreads.length > 0 && (
                  <TabPanel padding="0">
                    <OverviewUpdatesDrawerBody
                      threads={pinnedThreads}
                      refetchThreads={threadsQuery.refetch}
                    />
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export const OverviewUpdatesDrawer = (
  props: OverviewUpdatesDrawerBaseProps,
) => (
  <ErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return (
        // Disabled icon button as fallback UI
        <IconButtonV2
          isDisabled
          variant="primary-muted"
          placement="bottom"
          aria-label="Updates"
          icon={<Icon icon="message-lines" />}
        />
      );
    }}
  >
    <OverviewUpdatesDrawerBase {...props} />
  </ErrorBoundary>
);
